// Silent-uplink detection for the softphone's live call. Pure: use-softphone
// owns the timer, the getStats reads and the telemetry; this module only
// decides.
//
// The fault: the peer connection is fully connected and the agent hears the
// prospect, but the browser sends the switch no audio (a mic that stopped
// producing, a track the OS took away), so the prospect hears silence. None
// of the connection states move, so the stuck-media watch never sees it.
// FreeSWITCH's inbound packet count on the agent leg matches the browser's
// outbound-rtp packetsSent packet for packet, so a flat packetsSent is the
// switch hearing nothing.

// Same cadence as the stuck-media probe's two readings.
export const SILENT_UPLINK_TICK_MS = 2_000;

// A sender at 20 ms ptime puts out ~250 packets in this window; a stray few
// over the whole of it is still silence to the prospect.
export const SILENT_UPLINK_MS = 5_000;
const SILENT_UPLINK_MAX_SENT = 5;

// The downlink must be carrying audio over the same window (~1 s of it at
// 20 ms ptime), so a line that is dead both ways, which the stuck-media and
// ICE paths own, is not reported as the agent's mic.
const SILENT_UPLINK_MIN_RECEIVED = 50;

// Why the uplink is not being judged right now. Each one is a state where a
// silent sender is expected or the counters say nothing: the agent muted
// themselves, no prospect is bridged yet (early media and a parked pool line
// included), the switch put the line on hold (the transceiver stopped
// sending), or getStats had no reading.
export type SilentUplinkBlock = 'muted' | 'not_bridged' | 'on_hold' | 'no_stats';

interface UplinkReading {
  atMs: number;
  packetsSent: number;
  packetsReceived: number;
}

export interface SilentUplinkWatch {
  // Where the current window starts, and the latest reading; null while
  // blocked.
  base: UplinkReading | null;
  last: UplinkReading | null;
  flagged: boolean;
}

export const IDLE_SILENT_UPLINK_WATCH: SilentUplinkWatch = { base: null, last: null, flagged: false };

export type SilentUplinkEvent =
  | { type: 'detected'; silentMs: number; packetsSent: number; packetsReceived: number }
  // A flagged uplink that is no longer judged silent: packets are going out
  // again, or a block (mute, hang-up, hold) took over. `silentMs` runs from
  // the start of the silent window to now.
  | { type: 'cleared'; silentMs: number; reason: 'flowing' | 'ended' | SilentUplinkBlock };

// The line itself is gone (hang-up, runtime shutdown): a silence still
// flagged ends with it.
export function endUplinkWatch(watch: SilentUplinkWatch, nowMs: number): SilentUplinkEvent | null {
  return watch.flagged && watch.base ? { type: 'cleared', silentMs: nowMs - watch.base.atMs, reason: 'ended' } : null;
}

// Before the verdict, the whole window may carry only a stray few packets.
// After it, the uplink counts as back once a single reading moves by more
// than that: a trickle of strays over a long silence must not end it.
export function assessUplink(
  watch: SilentUplinkWatch,
  input: {
    nowMs: number;
    blockedBy: SilentUplinkBlock | null;
    packetsSent: number | null;
    packetsReceived: number | null;
  }
): { watch: SilentUplinkWatch; event: SilentUplinkEvent | null } {
  const { nowMs, packetsSent, packetsReceived } = input;
  const { base, last } = watch;
  if (input.blockedBy !== null || packetsSent === null || packetsReceived === null) {
    const event = watch.flagged && base
      ? { type: 'cleared' as const, silentMs: nowMs - base.atMs, reason: input.blockedBy ?? 'no_stats' }
      : null;
    return { watch: IDLE_SILENT_UPLINK_WATCH, event };
  }
  const reading = { atMs: nowMs, packetsSent, packetsReceived };
  // Counters only go down on a different stream (a replaced peer connection
  // or sender): start over from this one.
  if (!base || !last || packetsSent < last.packetsSent || packetsReceived < last.packetsReceived) {
    return { watch: { base: reading, last: reading, flagged: false }, event: null };
  }
  const silentMs = nowMs - base.atMs;
  if (watch.flagged) {
    if (packetsSent - last.packetsSent > SILENT_UPLINK_MAX_SENT) {
      return { watch: { base: reading, last: reading, flagged: false }, event: { type: 'cleared', silentMs, reason: 'flowing' } };
    }
    return { watch: { ...watch, last: reading }, event: null };
  }
  const sent = packetsSent - base.packetsSent;
  if (sent > SILENT_UPLINK_MAX_SENT) {
    return { watch: { base: reading, last: reading, flagged: false }, event: null };
  }
  const received = packetsReceived - base.packetsReceived;
  if (silentMs >= SILENT_UPLINK_MS && received >= SILENT_UPLINK_MIN_RECEIVED) {
    return {
      watch: { base, last: reading, flagged: true },
      event: { type: 'detected', silentMs, packetsSent: sent, packetsReceived: received },
    };
  }
  return { watch: { ...watch, last: reading }, event: null };
}

// The sending side of the line as the transceiver negotiated it. Only an
// answered hold (sendonly/inactive from the switch, recvonly/inactive here)
// stops the browser sending on purpose; before negotiation finishes there is
// no current direction and the line is judged on its counters.
export function uplinkOnHold(currentDirection: string | null | undefined): boolean {
  return currentDirection === 'recvonly' || currentDirection === 'inactive';
}
